import {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";

import {
  createDepot,
  deleteDepot,
  getDepots,
  getWarehouses,
  updateDepot,
} from "../../api/client.js";

import {
  ConfirmDialog,
  Dialog,
} from "../Dialog.jsx";

import { SortableHeader } from "../SortableHeader.jsx";
import { useSortableData } from "../../hooks/useSortableData.js";
import {
  DataMasterActiveFilters,
  DataMasterKpiCard,
  DataMasterKpiStrip,
  DataMasterRecordFooter,
  DataMasterRowActions,
  DataMasterSection,
  DataMasterTableShell,
  DataMasterToolbar,
} from "./DataMasterLayout.jsx";

const EMPTY_FORM = {
  depotCode: "",
  depotName: "",
  addressText: "",
  latitude: "",
  longitude: "",
  coordinateStatus: "MISSING",
  accurateWarehouseId: "",
  isDefault: false,
  active: true,
};

const COORDINATE_LABELS = {
  VALID: "Valid",
  NEEDS_REVIEW: "Perlu diperiksa",
  MISSING: "Belum tersedia",
};

function normalizeCoordinateStatus(value) {
  const normalized = String(value || "MISSING")
    .trim()
    .toUpperCase();

  return ["VALID", "NEEDS_REVIEW", "MISSING"].includes(normalized)
    ? normalized
    : "MISSING";
}

function formatCoordinate(value) {
  if (value === null || value === undefined || value === "") {
    return "—";
  }

  const numeric = Number(value);

  if (!Number.isFinite(numeric)) {
    return "—";
  }

  return numeric
    .toFixed(7)
    .replace(/0+$/, "")
    .replace(/\.$/, "");
}

function formatTimestamp(value) {
  if (!value) return "—";

  const parsed = new Date(value);

  if (Number.isNaN(parsed.getTime())) {
    return "—";
  }

  return new Intl.DateTimeFormat("id-ID", {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(parsed);
}

function CoordinateStatusBadge({ status }) {
  const normalized = normalizeCoordinateStatus(status);

  return (
    <span
      className={`coordinate-status coordinate-status--${normalized
        .toLowerCase()
        .replaceAll("_", "-")}`}
    >
      {COORDINATE_LABELS[normalized]}
    </span>
  );
}

function ActiveBadge({ active }) {
  return (
    <span
      className={`master-active master-active--${active ? "yes" : "no"}`}
    >
      {active ? "Aktif" : "Nonaktif"}
    </span>
  );
}

function mapDepotToForm(depot) {
  const hasCoordinates =
    depot.latitude !== null &&
    depot.latitude !== undefined &&
    depot.longitude !== null &&
    depot.longitude !== undefined;

  return {
    id: depot.id,
    depotCode: depot.depotCode || "",
    depotName: depot.depotName || "",
    addressText: depot.addressText || "",
    latitude: hasCoordinates ? String(depot.latitude) : "",
    longitude: hasCoordinates ? String(depot.longitude) : "",
    coordinateStatus: hasCoordinates
      ? normalizeCoordinateStatus(depot.coordinateStatus) === "VALID"
        ? "VALID"
        : "NEEDS_REVIEW"
      : "MISSING",
    accurateWarehouseId: depot.accurateWarehouseId
      ? String(depot.accurateWarehouseId)
      : "",
    isDefault: depot.isDefault === true,
    active: depot.active !== false,
  };
}

function validateDepotForm(form) {
  if (!String(form.depotCode || "").trim()) {
    return "Kode depot wajib diisi.";
  }

  if (!String(form.depotName || "").trim()) {
    return "Nama depot wajib diisi.";
  }

  if (form.isDefault && !form.active) {
    return "Depot default harus berstatus aktif.";
  }

  const latitudeFilled = String(form.latitude ?? "").trim() !== "";
  const longitudeFilled = String(form.longitude ?? "").trim() !== "";

  if (latitudeFilled !== longitudeFilled) {
    return "Latitude dan longitude wajib diisi bersamaan.";
  }

  if (!latitudeFilled) {
    return "Latitude dan longitude depot wajib diisi agar dapat digunakan pada peta.";
  }

  const latitude = Number(String(form.latitude).replace(",", "."));
  const longitude = Number(String(form.longitude).replace(",", "."));

  if (!Number.isFinite(latitude) || latitude < -90 || latitude > 90) {
    return "Latitude harus berupa angka antara -90 sampai 90.";
  }

  if (!Number.isFinite(longitude) || longitude < -180 || longitude > 180) {
    return "Longitude harus berupa angka antara -180 sampai 180.";
  }

  return "";
}

function buildDepotPayload(form) {
  const latitudeText = String(form.latitude ?? "").trim();
  const longitudeText = String(form.longitude ?? "").trim();
  const hasCoordinates = Boolean(latitudeText && longitudeText);

  return {
    depotCode: String(form.depotCode || "").trim(),
    depotName: String(form.depotName || "").trim(),
    addressText: String(form.addressText || "").trim(),
    latitude: hasCoordinates
      ? Number(latitudeText.replace(",", "."))
      : null,
    longitude: hasCoordinates
      ? Number(longitudeText.replace(",", "."))
      : null,
    coordinateSource: hasCoordinates ? "MANUAL" : null,
    coordinateStatus: hasCoordinates
      ? form.coordinateStatus === "VALID"
        ? "VALID"
        : "NEEDS_REVIEW"
      : "MISSING",
    accurateWarehouseId: form.accurateWarehouseId
      ? Number(form.accurateWarehouseId)
      : null,
    isDefault: Boolean(form.isDefault),
    active: Boolean(form.active),
  };
}

function DepotForm({ form, onChange, warehouses }) {
  const hasCoordinates = Boolean(
    String(form.latitude ?? "").trim() &&
      String(form.longitude ?? "").trim(),
  );

  function updateField(field, value) {
    const next = {
      ...form,
      [field]: value,
    };

    if (field === "latitude" || field === "longitude") {
      const pairFilled = Boolean(
        String(next.latitude ?? "").trim() &&
          String(next.longitude ?? "").trim(),
      );

      next.coordinateStatus = pairFilled
        ? next.coordinateStatus === "MISSING"
          ? "NEEDS_REVIEW"
          : next.coordinateStatus
        : "MISSING";
    }

    if (field === "isDefault" && value) {
      next.active = true;
    }

    onChange(next);
  }

  return (
    <div className="depot-dialog-form">
      <section className="depot-dialog-section">
        <header>
          <h3>Identitas Depot</h3>
          <p>Kode digunakan sebagai referensi internal Route Planning.</p>
        </header>

        <div className="depot-dialog-grid">
          <label>
            <span>Kode Depot</span>
            <input
              autoFocus
              onChange={(event) =>
                updateField("depotCode", event.target.value)
              }
              placeholder="Contoh: GAS-SMG"
              required
              value={form.depotCode}
            />
          </label>

          <label>
            <span>Nama Depot</span>
            <input
              onChange={(event) =>
                updateField("depotName", event.target.value)
              }
              placeholder="Contoh: Gudang Semarang"
              required
              value={form.depotName}
            />
          </label>

          <label className="depot-dialog-grid__full">
            <span>Alamat Depot</span>
            <textarea
              onChange={(event) =>
                updateField("addressText", event.target.value)
              }
              placeholder="Alamat lengkap depot"
              rows={3}
              value={form.addressText}
            />
          </label>
        </div>
      </section>

      <section className="depot-dialog-section">
        <header>
          <h3>Koordinat</h3>
          <p>Gunakan format angka desimal dengan titik atau koma.</p>
        </header>

        <div className="depot-dialog-grid depot-dialog-grid--coordinate">
          <label>
            <span>Latitude</span>
            <input
              inputMode="decimal"
              onChange={(event) =>
                updateField("latitude", event.target.value)
              }
              placeholder="-7.008540"
              required
              value={form.latitude}
            />
          </label>

          <label>
            <span>Longitude</span>
            <input
              inputMode="decimal"
              onChange={(event) =>
                updateField("longitude", event.target.value)
              }
              placeholder="110.347273"
              required
              value={form.longitude}
            />
          </label>

          <label>
            <span>Status Koordinat</span>
            <select
              disabled={!hasCoordinates}
              onChange={(event) =>
                updateField("coordinateStatus", event.target.value)
              }
              value={form.coordinateStatus}
            >
              <option value="NEEDS_REVIEW">Perlu diperiksa</option>
              <option value="VALID">Valid</option>
              {!hasCoordinates && (
                <option value="MISSING">Belum tersedia</option>
              )}
            </select>
          </label>
        </div>

        <div className="depot-coordinate-preview">
          <span>Koordinat yang akan disimpan</span>
          <strong>
            {hasCoordinates
              ? `${formatCoordinate(
                  Number(String(form.latitude).replace(",", ".")),
                )}, ${formatCoordinate(
                  Number(String(form.longitude).replace(",", ".")),
                )}`
              : "Belum lengkap"}
          </strong>
        </div>
      </section>

      <section className="depot-dialog-section">
        <header>
          <h3>Integrasi Gudang Accurate</h3>
          <p>Gudang ini menjadi pilihan default saat menyiapkan Delivery Order dari Fleet Order.</p>
        </header>

        <div className="depot-dialog-grid">
          <label className="depot-dialog-grid__full">
            <span>Gudang Accurate Terkait</span>
            <select
              onChange={(event) =>
                updateField("accurateWarehouseId", event.target.value)
              }
              value={form.accurateWarehouseId || ""}
            >
              <option value="">Belum dipetakan</option>
              {(warehouses || [])
                .filter((warehouse) =>
                  warehouse.active ||
                  String(warehouse.warehouseId) === String(form.accurateWarehouseId || ""),
                )
                .map((warehouse) => (
                  <option
                    disabled={!warehouse.active}
                    key={warehouse.warehouseId}
                    value={warehouse.warehouseId}
                  >
                    {warehouse.warehouseName}
                    {!warehouse.active ? " · Nonaktif" : ""}
                  </option>
                ))}
            </select>
            <small>Opsional untuk perjalanan, tetapi dibutuhkan agar Pembuatan DO memilih gudang otomatis.</small>
          </label>
        </div>
      </section>

      <section className="depot-dialog-section">
        <header>
          <h3>Penggunaan</h3>
          <p>Depot default menjadi titik awal dan akhir perjalanan.</p>
        </header>

        <div className="depot-option-grid">
          <label className="depot-option-card">
            <input
              checked={form.isDefault}
              onChange={(event) =>
                updateField("isDefault", event.target.checked)
              }
              type="checkbox"
            />

            <span>
              <strong>Jadikan depot default</strong>
              <small>Dipakai sebagai titik utama peta dan optimasi rute.</small>
            </span>
          </label>

          <label className="depot-option-card">
            <input
              checked={form.active}
              disabled={form.isDefault}
              onChange={(event) =>
                updateField("active", event.target.checked)
              }
              type="checkbox"
            />

            <span>
              <strong>Depot aktif</strong>
              <small>Depot dapat dipakai oleh proses operasional.</small>
            </span>
          </label>
        </div>
      </section>
    </div>
  );
}

export function DepotsTab() {
  const EMPTY_FILTERS = {
    search: "",
    activeStatus: "",
    coordinateStatus: "",
    warehouseMapping: "",
  };

  const [depots, setDepots] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [draftFilters, setDraftFilters] = useState(EMPTY_FILTERS);
  const [form, setForm] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");

  const filteredDepots = useMemo(() => {
    const search = filters.search.trim().toLowerCase();

    return depots.filter((depot) => {
      const coordinateStatus = normalizeCoordinateStatus(depot.coordinateStatus);
      const matchesSearch =
        !search ||
        [depot.depotCode, depot.depotName, depot.addressText]
          .filter(Boolean)
          .some((value) => String(value).toLowerCase().includes(search));
      const matchesActive =
        !filters.activeStatus ||
        (filters.activeStatus === "ACTIVE" ? depot.active : !depot.active);
      const matchesCoordinate =
        !filters.coordinateStatus ||
        coordinateStatus === filters.coordinateStatus;
      const matchesWarehouseMapping =
        !filters.warehouseMapping ||
        (filters.warehouseMapping === "MAPPED"
          ? depot.warehouseMappingStatus === "MAPPED"
          : depot.warehouseMappingStatus !== "MAPPED");

      return (
        matchesSearch &&
        matchesActive &&
        matchesCoordinate &&
        matchesWarehouseMapping
      );
    });
  }, [depots, filters]);

  const {
    sortedItems,
    sort,
    toggleSort,
  } = useSortableData(filteredDepots, "depotName");

  const summary = useMemo(() => ({
    total: filteredDepots.length,
    active: filteredDepots.filter((depot) => depot.active).length,
    valid: filteredDepots.filter(
      (depot) => normalizeCoordinateStatus(depot.coordinateStatus) === "VALID",
    ).length,
    defaultCount: filteredDepots.filter((depot) => depot.isDefault).length,
    mapped: filteredDepots.filter(
      (depot) => depot.warehouseMappingStatus === "MAPPED",
    ).length,
  }), [filteredDepots]);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");

    try {
      const [depotPayload, warehousePayload] = await Promise.all([
        getDepots(),
        getWarehouses(),
      ]);
      setDepots(depotPayload.depots || []);
      setWarehouses(warehousePayload.warehouses || []);
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  function applyFilters(event) {
    event.preventDefault();
    setFilters({
      ...draftFilters,
      search: draftFilters.search.trim(),
    });
  }

  function clearFilters() {
    setDraftFilters(EMPTY_FILTERS);
    setFilters(EMPTY_FILTERS);
  }

  function openCreateDialog() {
    setMessage("");
    setError("");
    setForm({ ...EMPTY_FORM });
  }

  function openEditDialog(depot) {
    setMessage("");
    setError("");
    setForm(mapDepotToForm(depot));
  }

  function closeDialog() {
    if (busy) return;
    setForm(null);
  }

  async function saveDepot(event) {
    event.preventDefault();

    const validationError = validateDepotForm(form);

    if (validationError) {
      setError(validationError);
      return;
    }

    setBusy(true);
    setMessage("");
    setError("");

    try {
      const response = form.id
        ? await updateDepot(form.id, buildDepotPayload(form))
        : await createDepot(buildDepotPayload(form));

      setMessage(
        response.message ||
          (form.id
            ? "Depot berhasil diperbarui."
            : "Depot berhasil ditambahkan."),
      );

      setForm(null);
      await load();
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setBusy(false);
    }
  }

  async function toggleDepotActive(depot) {
    const next = {
      ...mapDepotToForm(depot),
      active: !depot.active,
      isDefault: depot.isDefault && depot.active ? false : depot.isDefault,
    };

    setBusy(true);
    setMessage("");
    setError("");

    try {
      const response = await updateDepot(
        depot.id,
        buildDepotPayload(next),
      );

      setMessage(
        response.message ||
          `Depot berhasil ${depot.active ? "dinonaktifkan" : "diaktifkan"}.`,
      );

      await load();
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setBusy(false);
    }
  }

  async function setAsDefault(depot) {
    const next = {
      ...mapDepotToForm(depot),
      active: true,
      isDefault: true,
    };

    setBusy(true);
    setMessage("");
    setError("");

    try {
      const response = await updateDepot(
        depot.id,
        buildDepotPayload(next),
      );

      setMessage(
        response.message || "Depot default berhasil diperbarui.",
      );

      await load();
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setBusy(false);
    }
  }

  async function confirmDelete() {
    setBusy(true);
    setMessage("");
    setError("");

    try {
      const response = await deleteDepot(deleteTarget.id);

      setMessage(response.message || "Depot berhasil dihapus.");
      setDeleteTarget(null);
      await load();
    } catch (requestError) {
      setError(requestError.message);
      setDeleteTarget(null);
    } finally {
      setBusy(false);
    }
  }

  return (
    <DataMasterSection
      actions={
        <button
          className="primary-button"
          onClick={openCreateDialog}
          type="button"
        >
          Tambah Depot
        </button>
      }
      className="depot-master"
      error={error}
      message={message}
      subtitle="Kelola titik awal dan akhir perjalanan serta depot default untuk peta dan optimasi rute."
      title="Depot"
    >
      <DataMasterKpiStrip label="Ringkasan depot">
        <DataMasterKpiCard
          helper="Hasil filter aktif"
          icon="database"
          label="Total depot"
          value={summary.total}
        />
        <DataMasterKpiCard
          helper="Dapat dipakai operasional"
          icon="check"
          label="Depot aktif"
          tone="success"
          value={summary.active}
        />
        <DataMasterKpiCard
          helper="Titik awal dan akhir utama"
          icon="location"
          label="Depot default"
          tone="info"
          value={summary.defaultCount}
        />
        <DataMasterKpiCard
          helper="Siap digunakan di peta"
          icon="route"
          label="Koordinat valid"
          tone="success"
          value={summary.valid}
        />
        <DataMasterKpiCard
          helper="Terhubung ke gudang Accurate"
          icon="database"
          label="Mapping gudang"
          tone="info"
          value={summary.mapped}
        />
      </DataMasterKpiStrip>

      <DataMasterToolbar className="dm-location-filter-bar dm-location-filter-bar--compact" onSubmit={applyFilters}>
        <input
          aria-label="Cari depot"
          onChange={(event) =>
            setDraftFilters((current) => ({ ...current, search: event.target.value }))
          }
          placeholder="Cari kode, nama, atau alamat depot"
          type="search"
          value={draftFilters.search}
        />
        <select
          aria-label="Status depot"
          onChange={(event) =>
            setDraftFilters((current) => ({ ...current, activeStatus: event.target.value }))
          }
          value={draftFilters.activeStatus}
        >
          <option value="">Semua status data</option>
          <option value="ACTIVE">Aktif</option>
          <option value="INACTIVE">Nonaktif</option>
        </select>
        <select
          aria-label="Status koordinat depot"
          onChange={(event) =>
            setDraftFilters((current) => ({ ...current, coordinateStatus: event.target.value }))
          }
          value={draftFilters.coordinateStatus}
        >
          <option value="">Semua status koordinat</option>
          <option value="VALID">Valid</option>
          <option value="NEEDS_REVIEW">Perlu diperiksa</option>
          <option value="MISSING">Belum tersedia</option>
        </select>
        <select
          aria-label="Mapping gudang Accurate"
          onChange={(event) =>
            setDraftFilters((current) => ({ ...current, warehouseMapping: event.target.value }))
          }
          value={draftFilters.warehouseMapping}
        >
          <option value="">Semua mapping gudang</option>
          <option value="MAPPED">Sudah dipetakan</option>
          <option value="UNMAPPED">Belum dipetakan</option>
        </select>
        <button className="secondary-button" disabled={loading} type="submit">Terapkan</button>
        <button className="text-button" disabled={loading} onClick={clearFilters} type="button">
          Bersihkan
        </button>
      </DataMasterToolbar>

      <DataMasterActiveFilters
        filters={[
          { label: "Pencarian", value: filters.search },
          {
            label: "Status",
            value: { ACTIVE: "Aktif", INACTIVE: "Nonaktif" }[filters.activeStatus] || "",
          },
          {
            label: "Koordinat",
            value: {
              VALID: "Valid",
              NEEDS_REVIEW: "Perlu diperiksa",
              MISSING: "Belum tersedia",
            }[filters.coordinateStatus] || "",
          },
          {
            label: "Gudang Accurate",
            value: {
              MAPPED: "Sudah dipetakan",
              UNMAPPED: "Belum dipetakan",
            }[filters.warehouseMapping] || "",
          },
        ]}
        onClear={clearFilters}
      />

      <DataMasterTableShell
        className="depot-table-wrap"
        emptyText="Belum ada depot yang sesuai filter."
        loading={loading}
        mode="compact"
        rowCount={sortedItems.length}
      >
        <table className="master-table master-table--depots-compact">
          <thead>
            <tr>
              <SortableHeader column="depotName" sort={sort} toggleSort={toggleSort}>Depot</SortableHeader>
              <SortableHeader column="addressText" sort={sort} toggleSort={toggleSort}>Lokasi</SortableHeader>
              <SortableHeader column="coordinateStatus" sort={sort} toggleSort={toggleSort}>Koordinat</SortableHeader>
              <SortableHeader column="accurateWarehouseName" sort={sort} toggleSort={toggleSort}>Gudang Accurate</SortableHeader>
              <SortableHeader column="active" sort={sort} toggleSort={toggleSort}>Penggunaan</SortableHeader>
              <SortableHeader column="updatedAt" sort={sort} toggleSort={toggleSort}>Diperbarui</SortableHeader>
              <th className="sticky-action-cell master-action-cell">Aksi</th>
            </tr>
          </thead>
          <tbody>
            {!loading && sortedItems.map((depot) => (
              <tr className={depot.isDefault ? "master-row--default-depot" : ""} key={depot.id}>
                <td>
                  <div className="dm-cell-stack dm-cell-stack--primary">
                    <strong>{depot.depotName}</strong>
                    <span>{depot.depotCode}</span>
                    {depot.isDefault && <small>Depot utama</small>}
                  </div>
                </td>
                <td>
                  <div className="dm-cell-stack">
                    <strong>{depot.addressText || "Alamat belum dilengkapi"}</strong>
                    <span>{depot.active ? "Tersedia untuk perjalanan" : "Tidak dipakai operasional"}</span>
                  </div>
                </td>
                <td>
                  <div className="dm-location-coordinate-cell">
                    <div className="depot-coordinate-cell">
                      <strong>{formatCoordinate(depot.latitude)}, {formatCoordinate(depot.longitude)}</strong>
                      <span>{depot.coordinateSource || "Belum ada sumber"}</span>
                    </div>
                    <CoordinateStatusBadge status={depot.coordinateStatus} />
                  </div>
                </td>
                <td>
                  <div className="dm-cell-stack">
                    <strong>{depot.accurateWarehouseName || "Belum dipetakan"}</strong>
                    <span>
                      {depot.warehouseMappingStatus === "MAPPED"
                        ? `ID ${depot.accurateWarehouseId}`
                        : depot.warehouseMappingStatus === "WAREHOUSE_INACTIVE"
                          ? "Gudang nonaktif"
                          : "Pembuatan DO memakai fallback"}
                    </span>
                  </div>
                </td>
                <td>
                  <div className="dm-cell-stack">
                    <ActiveBadge active={depot.active} />
                    {depot.isDefault ? (
                      <span className="depot-default-badge">Default</span>
                    ) : (
                      <button
                        className="text-button depot-set-default"
                        disabled={busy || !depot.active}
                        onClick={() => setAsDefault(depot)}
                        type="button"
                      >
                        Jadikan default
                      </button>
                    )}
                  </div>
                </td>
                <td>{formatTimestamp(depot.updatedAt)}</td>
                <td className="sticky-action-cell master-action-cell">
                  <DataMasterRowActions
                    active={depot.active}
                    busy={busy}
                    onDelete={() => setDeleteTarget(depot)}
                    onEdit={() => openEditDialog(depot)}
                    onToggle={() => toggleDepotActive(depot)}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </DataMasterTableShell>

      <DataMasterRecordFooter count={sortedItems.length} />

      {form && (
        <Dialog
          className="dialog-card--depot-editor"
          onClose={closeDialog}
          title={form.id ? `Edit ${form.depotName}` : "Tambah Depot"}
        >
          <form onSubmit={saveDepot}>
            <div className="dialog-card__body depot-dialog-body">
              {error && (
                <div className="error-banner" role="alert">
                  {error}
                </div>
              )}

              <DepotForm form={form} onChange={setForm} warehouses={warehouses} />
            </div>

            <footer className="dialog-card__actions">
              <button
                className="secondary-button"
                disabled={busy}
                onClick={closeDialog}
                type="button"
              >
                Batal
              </button>

              <button
                className="primary-button"
                disabled={busy}
                type="submit"
              >
                {busy
                  ? "Menyimpan…"
                  : form.id
                    ? "Simpan Perubahan"
                    : "Tambah Depot"}
              </button>
            </footer>
          </form>
        </Dialog>
      )}

      {deleteTarget && (
        <ConfirmDialog
          busy={busy}
          confirmLabel="Hapus Depot"
          description={`Hapus depot “${deleteTarget.depotName}”? Tindakan ini tidak dapat dibatalkan.`}
          onCancel={() => setDeleteTarget(null)}
          onConfirm={confirmDelete}
          title="Hapus Depot"
        />
      )}
    </DataMasterSection>
  );
}
